import { useCallback, useEffect, useRef, useState } from 'react'
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { LIVE_URL } from '../../../../configurations/initUrls'

/**
 * Ek hi jagah par saara SignalR logic.
 *
 * Kya theek karta hai:
 *  - withAutomaticReconnect + manual retry backoff (pehle kuch bhi nahi tha)
 *  - reconnect ke baad dobara JoinRoom (SignalR reconnect par group membership chali jati hai,
 *    isliye connection "green" dikhta tha par messages nahi aate the)
 *  - app background se wapas aane par / net wapas aane par turant reconnect
 *  - purani connection hamesha stop hoti hai (StrictMode double-mount, reConnect() duplicate)
 *  - onMessage hamesha latest render ka hota hai (stale closure nahi)
 *  - sendMessage disconnect hone par chup-chaap fail nahi hota: pehle reconnect try, phir false return
 *
 * @param {object}   opts
 * @param {string}   opts.user      hub user name (e.g. "abc_12_Reception")
 * @param {string}   opts.room      room name (e.g. "abc_12")
 * @param {function} opts.onMessage (message: string, fromUser: string) => void
 * @param {function} opts.onRejoin  reconnect ke baad data refresh karne ke liye
 * @param {boolean}  opts.enabled   false ho to connect nahi karega
 */
export default function useLiveRoom({ user, room, onMessage, onRejoin, enabled = true }) {
  const RETRY_DELAYS = [0, 2000, 5000, 10000, 30000]

  const [isConnected, setIsConnected] = useState(false)
  const [users, setUsers] = useState([])

  const connectionRef = useRef(null)
  const startingRef = useRef(false)
  const mountedRef = useRef(true)
  const retryTimerRef = useRef(null)
  const retryIndexRef = useRef(0)
  const connectRef = useRef(null)

  const onMessageRef = useRef(onMessage)
  const onRejoinRef = useRef(onRejoin)
  const userRef = useRef(user)
  const roomRef = useRef(room)

  // har render par latest handlers/naam ref me rakh lo -> handler kabhi purana nahi hoga
  useEffect(() => {
    onMessageRef.current = onMessage
    onRejoinRef.current = onRejoin
    userRef.current = user
    roomRef.current = room
  })

  const clearRetry = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }

  const scheduleRetry = useCallback(() => {
    if (!mountedRef.current) return
    clearRetry()
    const i = Math.min(retryIndexRef.current, RETRY_DELAYS.length - 1)
    const delay = RETRY_DELAYS[i] || 1000
    retryIndexRef.current += 1
    retryTimerRef.current = setTimeout(() => {
      if (connectRef.current) connectRef.current()
    }, delay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const connect = useCallback(async () => {
    if (!mountedRef.current || startingRef.current) return
    startingRef.current = true
    clearRetry()

    // purani connection hamesha band karo
    const prev = connectionRef.current
    connectionRef.current = null
    setIsConnected(false)
    if (prev) {
      try { await prev.stop() } catch (e) { /* ignore */ }
    }

    const conn = new HubConnectionBuilder()
      .withUrl(LIVE_URL)
      .withAutomaticReconnect(RETRY_DELAYS)
      .configureLogging(LogLevel.Warning)
      .build()

    conn.on('UsersInRoom', (u) => {
      if (mountedRef.current) setUsers(u || [])
    })

    conn.on('ReceiveMessage', (fromUser, message) => {
      if (!mountedRef.current) return
      if (onMessageRef.current) onMessageRef.current(String(message), fromUser)
    })

    conn.onreconnecting(() => {
      if (mountedRef.current) setIsConnected(false)
    })

    conn.onreconnected(async () => {
      // ZAROORI: reconnect par group membership khatam ho jati hai
      try {
        await conn.invoke('JoinRoom', { user: userRef.current, room: roomRef.current })
      } catch (e) {
        console.warn('[live] rejoin failed', e)
        scheduleRetry()
        return
      }
      if (!mountedRef.current) return
      retryIndexRef.current = 0
      setIsConnected(true)
      if (onRejoinRef.current) onRejoinRef.current()
    })

    conn.onclose(() => {
      if (!mountedRef.current) return
      setIsConnected(false)
      scheduleRetry()
    })

    try {
      await conn.start()
      await conn.invoke('JoinRoom', { user: userRef.current, room: roomRef.current })
      if (!mountedRef.current) {
        try { await conn.stop() } catch (e) { /* ignore */ }
        return
      }
      connectionRef.current = conn
      retryIndexRef.current = 0
      setIsConnected(true)
    } catch (e) {
      console.warn('[live] connect failed', e)
      if (mountedRef.current) {
        setIsConnected(false)
        scheduleRetry()
      }
    } finally {
      startingRef.current = false
    }
  }, [scheduleRetry])

  connectRef.current = connect

  useEffect(() => {
    mountedRef.current = true
    if (enabled) connect()

    // Android WebView: app background se wapas aaye ya net wapas aaye to turant check
    const wakeUp = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      const conn = connectionRef.current
      if (!conn || conn.state !== HubConnectionState.Connected) {
        retryIndexRef.current = 0
        connect()
      }
    }

    document.addEventListener('visibilitychange', wakeUp)
    window.addEventListener('online', wakeUp)
    window.addEventListener('focus', wakeUp)

    return () => {
      mountedRef.current = false
      document.removeEventListener('visibilitychange', wakeUp)
      window.removeEventListener('online', wakeUp)
      window.removeEventListener('focus', wakeUp)
      clearRetry()
      const conn = connectionRef.current
      connectionRef.current = null
      if (conn) { try { conn.stop() } catch (e) { /* ignore */ } }
    }
  }, [connect, enabled])

  /** @returns {Promise<boolean>} true = bhej diya, false = nahi bheja ja saka */
  const sendMessage = useCallback(async (message) => {
    let conn = connectionRef.current
    if (!conn || conn.state !== HubConnectionState.Connected) {
      await connect()
      conn = connectionRef.current
    }
    if (!conn || conn.state !== HubConnectionState.Connected) {
      console.warn('[live] send skipped (not connected):', message)
      return false
    }
    try {
      await conn.invoke('SendMessage', String(message))
      return true
    } catch (e) {
      console.warn('[live] send failed:', message, e)
      scheduleRetry()
      return false
    }
  }, [connect, scheduleRetry])

  const reconnect = useCallback(() => {
    retryIndexRef.current = 0
    return connect()
  }, [connect])

  return { isConnected, users, sendMessage, reconnect }
}